import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Check as CheckIcon, Clear as ClearIcon, Sell } from '@mui/icons-material';
import { Box, Checkbox, Divider, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { DateTimePicker } from '@mui/x-date-pickers';

import useNotification from '../../../../core/hooks/use_notification';
import { MISC_DATE_ISO_FORMAT } from '../../../../core/constants';
import { Button, DeleteButton, SubmitButton } from '../../../common/button';
import { deletePromotionNotificationData, getPromotionNotificationData, updatePromotionNotificationData } from '../../../../core/utils/notification_utils';
import { getProductPromotionAction, getProductPromotionState, invalidateProductPromotion } from '../../../../features/price/get_product_promotion';
import { deleteProductPromotionAction } from '../../../../features/price/delete_product_promotion';
import { createProductPromotionsAction } from '../../../../features/price/create_product_promotions';
import { updateProductPromotionAction } from '../../../../features/price/update_product_promotion';

export default function ProductPromotionDialog({ resource, podId, refreshList }) {
    const [open, setOpen] = useState(false);
    const [discount, setDiscount] = useState('');
    const [timeFrom, setTimeFrom] = useState(null);
    const [timeTo, setTimeTo] = useState(null);
    const [activeExpirationPromotion, setActiveExpirationPromotion] = useState(false);
    const [productPromotionId, setProductPromotionId] = useState(false);

    const getProductPromotion = useSelector(getProductPromotionState);

    const dispatch = useDispatch();
    const { showNotification, showErrorMessage } = useNotification();

    const calculateDiscount = (base, percentage) => (parseFloat(base).toFixed(2) - parseFloat((percentage / 100 * base).toFixed(2))).toFixed(2);

    const loadPromotion = () => {
        dispatch(getProductPromotionAction({ productId: resource?.productId, podId }))
            .then(response => {
                showErrorMessage(getPromotionNotificationData(response));
                reloadValues(response?.payload);
            });
    };

    const reloadValues = (initialValue) => {
        setDiscount(initialValue?.discount ? parseFloat(initialValue?.discount).toFixed(2) : '');
        setTimeFrom(initialValue?.timeFrom ? moment(initialValue?.timeFrom) : null);
        setTimeTo(initialValue?.timeTo ? moment(initialValue?.timeTo) : null);
        setProductPromotionId(initialValue?.productPromotionId);
        setActiveExpirationPromotion(!initialValue?.excludeExpirationPromotion || false);
    };

    const onOpen = () => {
        loadPromotion();
        setOpen(true);
    };

    const onSubmit = () => {
        if (discount && timeFrom?._isValid && timeTo?._isValid) {
            const excludeExpirationPromotion = !activeExpirationPromotion;
            if (productPromotionId) {
                const form = {
                    timeFrom: moment(timeFrom).format(MISC_DATE_ISO_FORMAT),
                    timeTo: moment(timeTo).format(MISC_DATE_ISO_FORMAT),
                    discount,
                    excludeExpirationPromotion
                };

                dispatch(updateProductPromotionAction({ productPromotionId, form }))
                    .then(response => showNotification(updatePromotionNotificationData(response)));
            } else {
                const form = {
                    podIds: [podId],
                    timeFrom: moment(timeFrom).format(MISC_DATE_ISO_FORMAT),
                    timeTo: moment(timeTo).format(MISC_DATE_ISO_FORMAT),
                    discount,
                    excludeExpirationPromotion
                };

                dispatch(createProductPromotionsAction({ productId: resource?.productId, form }))
                    .then(response => showNotification(updatePromotionNotificationData(response)));
            }

            refreshList();
        }

        onClose();
    };

    const onDelete = () => {
        setDiscount('');
        setTimeFrom(null);
        setActiveExpirationPromotion(false);

        if (productPromotionId) {
            dispatch(deleteProductPromotionAction(productPromotionId))
                .then(response => showNotification(deletePromotionNotificationData(response)));
            refreshList();
        }
    };

    const onClose = () => {
        setOpen(false);
        dispatch(invalidateProductPromotion());
    };

    return (
        <div>
            <Sell onClick={ onOpen } cursor="pointer"/>
            <Dialog open={ open } onClose={ onClose } fullWidth={ true } maxWidth={ 'lg' }>
                <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Promocja na towar</DialogTitle>
                <Divider/>

                <DialogContent sx={ { py: 3 } }>
                    <Box sx={ {
                        p: 2,
                        backgroundColor: 'slate.50',
                        borderRadius: 2,
                        mb: 3,
                        border: theme => `1px solid ${ theme.palette.divider }`
                    } }>
                        <Typography variant="body1">
                            <b>{ resource?.productName }</b>
                        </Typography>
                    </Box>

                    {
                        getProductPromotion?.loading &&
                        <Box width="100%" sx={ { mb: 2 } }>
                            <LinearProgress/>
                        </Box>
                    }
                    {
                        getProductPromotion?.data &&
                        <TableContainer component={ Paper } variant="outlined">
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={ {
                                        '& th': {
                                            backgroundColor: 'slate.50',
                                            fontWeight: 600,
                                            lineHeight: 1.2
                                        }
                                    } }>
                                        <TableCell align="center" sx={ { minWidth: 100 } }>Czy wygen. przez akcje
                                            promocyjną</TableCell>
                                        <TableCell align="center">Cena zakupu</TableCell>
                                        <TableCell align="center">Cena bazowa</TableCell>
                                        <TableCell align="center" sx={ { minWidth: 100, maxWidth: 100 } }>Upust
                                            [%]</TableCell>
                                        <TableCell align="center">Cena promocyjna</TableCell>
                                        <TableCell align="center">Promocja od</TableCell>
                                        <TableCell align="center">Promocja do</TableCell>
                                        <TableCell align="center" sx={ { minWidth: 100 } }>Łączy się z prom. na krótką
                                            datę?</TableCell>
                                        <TableCell align="center"></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell align="center">
                                            {
                                                getProductPromotion?.data?.isPromotionCampaign
                                                    ? <CheckIcon color="success"/>
                                                    : <ClearIcon color="error"/>
                                            }
                                        </TableCell>
                                        <TableCell
                                            align="center">{ resource?.purchaseGrossPrice?.toFixed(2) }</TableCell>
                                        <TableCell align="center">{ resource?.basicGrossPrice?.toFixed(2) }</TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                value={ discount }
                                                type="number"
                                                inputProps={ { min: '0.01', max: '100.00', step: '0.01' } }
                                                onChange={ event => setDiscount(event.target.value) }
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={ { fontWeight: 600 } }>
                                            {
                                                getProductPromotion?.data?.promotionGrossPrice?.toFixed(2)
                                                || (discount ? calculateDiscount(resource?.basicGrossPrice, discount) : '')
                                            }
                                        </TableCell>
                                        <TableCell>
                                            <DateTimePicker
                                                value={ timeFrom }
                                                onChange={ newDate => setTimeFrom(newDate) }
                                                sx={ { width: 190 } }
                                                slotProps={ {
                                                    textField: {
                                                        size: 'small',
                                                        InputLabelProps: { shrink: true }
                                                    }
                                                } }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <DateTimePicker
                                                value={ timeTo }
                                                onChange={ newDate => setTimeTo(newDate) }
                                                sx={ { width: 190 } }
                                                slotProps={ {
                                                    textField: {
                                                        size: 'small',
                                                        InputLabelProps: { shrink: true }
                                                    }
                                                } }
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Checkbox
                                                checked={ activeExpirationPromotion }
                                                onChange={ (event) => setActiveExpirationPromotion(event.target.checked) }
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            {
                                                productPromotionId && !getProductPromotion?.data?.isPromotionCampaign &&
                                                <DeleteButton onDeleteClick={ () => onDelete() }/>
                                            }
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    }
                </DialogContent>

                <Divider/>
                <DialogActions sx={ { p: 2, px: 3, gap: 1 } }>
                    <Button variant="outlined" onClick={ onClose }>Anuluj</Button>
                    <SubmitButton onClick={ onSubmit }
                                  disabled={ !discount || !timeFrom || !timeTo }>Zapisz</SubmitButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}